<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, toRef, watch, type Ref } from 'vue'
import {
  MarDynamicGrid,
  MarEmptyState,
  confirmAction,
  notify,
  openModal,
  useViewport,
  type MarDynamicGridDrop,
  type MarDynamicGridItem,
  type MarDynamicGridMode,
  type MarDynamicGridPlacement
} from '@nimling/components-nimtech'
import { PageGapRem, type Page, type PageView, type View, type ViewLayout } from '@/types'
import { getPageLayout } from '@/stores/pages'
import { useViewsStore } from '@/stores/views'
import { useVariablesStore } from '@/stores/variables'
import { useSettingsStore } from '@/stores/settings'
import { getRangeQuery, usePageData } from '@/lib/data'
import { getViewMinimum, putResizing } from '@/lib/view'
import { intervalStep } from '@/lib/variables'
import { useRangeStore } from '@/stores/range'
import PlacementSettingsDialog from './dialogs/PlacementSettingsDialog.vue'
import PageTile from './PageTile.vue'
import ViewPreview from './ViewPreview.vue'

const props = withDefaults(defineProps<{
  page: Page
  pageViews: PageView[]
  editable?: boolean
  live?: boolean
  scroller?: HTMLElement | null
}>(), {
  editable: false,
  live: true
})

const VIEW_MIME = 'application/x-hvergel-view'


const viewsStore = useViewsStore()
const variablesStore = useVariablesStore()
const settingsStore = useSettingsStore()

const rootEl = ref<HTMLElement | null>(null)
const stageHeight = ref(0)
const remPx = ref(16)

const grid = computed(() => getPageLayout(props.page))
const mode = computed(() => grid.value.mode)
const single = computed(() => mode.value === 'single')
const fixed = computed(() => mode.value === 'fixed')
const columns = computed(() => (single.value ? 1 : grid.value.columns))
const maxRows = computed(() => (single.value ? 1 : fixed.value && !stacked.value ? grid.value.rows : Infinity))

const visible = computed(() => (single.value ? props.pageViews.slice(0, 1) : props.pageViews))
const hidden = computed(() => props.pageViews.length - visible.value.length)

const dropW = computed(() => (single.value ? 1 : Math.min(settingsStore.placementDefaults.w, columns.value)))
const dropH = computed(() => (single.value ? 1 : fixed.value ? Math.min(settingsStore.placementDefaults.h, grid.value.rows) : settingsStore.placementDefaults.h))

const gap = computed(() => (grid.value.bare ? 0 : Math.round(remPx.value * PageGapRem[grid.value.gap])))
const spacing = computed<[number, number]>(() => [gap.value, gap.value])

const variables = computed(() => variablesStore.getVariableValues(props.page.id))

const parseLayout = (raw: string): Partial<ViewLayout> | null => {
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const getLayout = (sv: PageView): ViewLayout => {
  const raw = sv.layout as Partial<ViewLayout> | string | null | undefined
  const parsed = typeof raw === 'string' ? parseLayout(raw) : raw
  if (parsed && typeof parsed === 'object') {
    return { x: Number(parsed.x) || 0, y: Number(parsed.y) || 0, w: Number(parsed.w) || 4, h: Number(parsed.h) || 3 }
  }
  return { x: 0, y: 0, w: 4, h: 3 }
}

const COLUMN_REM = 4

const { mobile } = useViewport()

const flow = computed(() => mode.value === 'dynamic' && !props.editable && !grid.value.scroll_x)

const pageScroll = computed(() => mode.value === 'dynamic' && !grid.value.scroll_x)

const stacked = computed(() => fixed.value && mobile.value)

const gridMode = computed<MarDynamicGridMode>(() => (flow.value || stacked.value ? 'flow' : 'fixed'))

const minColWidth = computed(() => (props.editable || single.value || grid.value.scroll_x ? 0 : Math.round(COLUMN_REM * remPx.value)))

const items = computed<MarDynamicGridItem[]>(() =>
  visible.value.map(sv => {
    if (single.value) return { id: sv.id, x: 0, y: 0, w: 1, h: 1 }
    const l = getLayout(sv)
    const w = Math.max(1, Math.min(l.w, columns.value))
    const least = getViewMinimum(viewsStore.viewIndex[sv.view_id] ?? {})
    const minH = Math.max(1, Math.min(Math.ceil((least.height * remPx.value + gap.value) / (rowHeight.value + gap.value)), maxRows.value))
    const h = Math.max(minH, Math.min(l.h, maxRows.value))
    return { id: sv.id, x: Math.max(0, Math.min(l.x, columns.value - w)), y: Math.max(0, l.y), w, h, minH, minWidth: least.width }
  })
)

const bottom = computed(() => items.value.reduce((max, i) => Math.max(max, i.y + i.h), 0))
const fillRows = computed(() => Math.max(1, fixed.value && !stacked.value ? grid.value.rows : single.value ? 1 : bottom.value))

const ROW_REM = 2

const rowHeight = computed(() => {
  if (mode.value === 'dynamic' || stacked.value) return Math.round(ROW_REM * parseFloat(getComputedStyle(document.documentElement).fontSize))
  const free = stageHeight.value - gap.value * 2 - (fillRows.value - 1) * gap.value
  return Math.max(1, Math.floor(free / fillRows.value))
})

const getPlacement = (id: string) => {
  const sv = visible.value.find(s => s.id === id)
  if (!sv) return null
  const view = viewsStore.viewIndex[sv.view_id]
  if (!view) return null
  return { sv, view }
}

const rangeStore = useRangeStore()
const pageId = computed(() => props.page.id)
const pageVariables = computed(() => (Array.isArray(props.page.variables) ? props.page.variables : []))
const dataQuery = computed(() => ({ ...getRangeQuery(rangeStore.state, variables.value), step: intervalStep(pageVariables.value, variables.value) ?? undefined }))

const LAZY_MOUNT = '100% 0px'
const LAZY_KEEP = '300% 0px'
const near = ref<Set<string>>(new Set())
const kept = ref<Set<string>>(new Set())
const lazy = computed(() => mode.value === 'dynamic')
let mountWatch: IntersectionObserver | null = null
let keepWatch: IntersectionObserver | null = null
const watched = new Map<string, HTMLElement>()

const putSeen = (target: Ref<Set<string>>, entries: IntersectionObserverEntry[]) => {
  const next = new Set(target.value)
  for (const entry of entries) {
    const id = (entry.target as HTMLElement).dataset.placement
    if (!id) continue
    if (entry.isIntersecting) next.add(id)
    else next.delete(id)
  }
  target.value = next
}

const startLazy = () => {
  mountWatch?.disconnect()
  keepWatch?.disconnect()
  mountWatch = null
  keepWatch = null
  const root = pageScroll.value ? props.scroller : rootEl.value
  if (!root) return
  mountWatch = new IntersectionObserver((entries) => putSeen(near, entries), { root, rootMargin: LAZY_MOUNT })
  keepWatch = new IntersectionObserver((entries) => putSeen(kept, entries), { root, rootMargin: LAZY_KEEP })
  for (const el of watched.values()) {
    mountWatch.observe(el)
    keepWatch.observe(el)
  }
}

const mounted = ref<Set<string>>(new Set())

watch([near, kept, lazy], () => {
  const next = new Set<string>()
  for (const id of mounted.value) if (kept.value.has(id)) next.add(id)
  for (const id of near.value) next.add(id)
  mounted.value = next
})

const isMounted = (id: string) => !lazy.value || mounted.value.has(id)

const { views: placementData } = usePageData(pageId, dataQuery, toRef(props, 'live'))

const watchTile = (id: string, el: Element | null) => {
  const held = watched.get(id)
  if (held === el) return
  if (held) {
    mountWatch?.unobserve(held)
    keepWatch?.unobserve(held)
    watched.delete(id)
  }
  if (!(el instanceof HTMLElement)) return
  watched.set(id, el)
  mountWatch?.observe(el)
  keepWatch?.observe(el)
}

const minWidth = computed(() => (grid.value.scroll_x ? `${columns.value * 7}rem` : undefined))

const persist = async (placements: MarDynamicGridPlacement[]) => {
  for (const p of placements) {
    const sv = visible.value.find(s => s.id === p.id)
    if (!sv) continue
    const cur = getLayout(sv)
    if (cur.x === p.x && cur.y === p.y && cur.w === p.w && cur.h === p.h) continue
    try {
      await viewsStore.putPageView({ ...sv, layout: { x: p.x, y: p.y, w: p.w, h: p.h } })
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Layout change failed')
    }
  }
}

const removePlacement = async (sv: PageView, view: View) => {
  const ok = await confirmAction({
    title: 'Remove placement',
    message: `Take "${view.name}" off ${props.page.name}?`,
    acceptLabel: 'Remove',
    severity: 'danger'
  })
  if (!ok) return
  try {
    await viewsStore.deletePageView(sv.id)
    notify('info', 'Placement removed')
  } catch (err) {
    notify('error', err instanceof Error ? err.message : 'Remove placement failed')
  }
}

const openPlacement = async (sv: PageView, view: View) => {
  await openModal<'saved'>({
    kind: 'dialog',
    component: PlacementSettingsDialog,
    props: { pageView: sv, view }
  })
}

const dropBlocked = computed(() => single.value && props.pageViews.length > 0)

const onDragOver = (e: DragEvent) => {
  if (props.editable && e.dataTransfer?.types.includes(VIEW_MIME)) putResizing()
}

const postPlacement = async (viewId: string, layout: ViewLayout) => {
  const view = viewsStore.viewIndex[viewId]
  if (!view) return
  try {
    await viewsStore.postPageView(props.page.id, {
      page_id: props.page.id,
      view_id: view.id,
      layout,
      config: settingsStore.placementDefaults.annotations ? null : { annotations: { hidden: true } }
    })
  } catch (err) {
    notify('error', err instanceof Error ? err.message : 'Add view failed')
  }
}

const onGridDrop = async (drop: MarDynamicGridDrop) => {
  await persist(drop.moved)
  await postPlacement(drop.data, { x: drop.placement.x, y: drop.placement.y, w: drop.placement.w, h: drop.placement.h })
}

const dragView = computed(() => (viewsStore.dragId ? viewsStore.viewIndex[viewsStore.dragId] ?? null : null))

const putDropBox = () => {
  const width = rootEl.value?.clientWidth ?? 0
  if (!width) return
  const colWidth = (width - gap.value * 2 - (columns.value - 1) * gap.value) / columns.value
  viewsStore.dropBox = {
    width: Math.round(colWidth * dropW.value + gap.value * (dropW.value - 1)),
    height: Math.round(rowHeight.value * dropH.value + gap.value * (dropH.value - 1))
  }
}

watch([columns, rowHeight, dropW, dropH, gap], putDropBox)

let sizing: ResizeObserver | null = null

const startSizing = () => {
  sizing?.disconnect()
  sizing = null
  const el = rootEl.value
  if (!el || mode.value === 'dynamic') return
  sizing = new ResizeObserver(entries => {
    stageHeight.value = entries[0]?.contentRect.height ?? 0
  })
  sizing.observe(el)
  stageHeight.value = el.clientHeight
}

watch(mode, startSizing)
watch([pageScroll, () => props.scroller], startLazy)

onMounted(() => {
  remPx.value = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
  startSizing()
  startLazy()
  putDropBox()
})

onBeforeUnmount(() => {
  sizing?.disconnect()
  sizing = null
  mountWatch?.disconnect()
  keepWatch?.disconnect()
})
</script>

<template>
  <div class="root" :class="{ 'root-flow': pageScroll }">
    <div
      ref="rootEl"
      class="page-grid"
      :class="{ 'page-grid-flow': pageScroll }"
      @dragover="onDragOver"
    >
      <MarDynamicGrid
        :items="items"
        :columns="stacked ? 1 : columns"
        :mode="gridMode"
        :min-col-width="minColWidth"
        :overflow="stacked"
        :row-height="rowHeight"
        :style="{ minWidth, minHeight: '100%' }"
        :max-rows="maxRows"
        :margin="spacing"
        :padding="spacing"
        :draggable="editable && !single"
        :resizable="editable && !single"
        :vertical-compact="mode === 'dynamic'"
        :drop-type="editable && !dropBlocked ? VIEW_MIME : ''"
        :empty="editable ? 'Drag a view from the views panel onto the grid' : 'Edit the page to place a view'"
        :drop-size="{ w: dropW, h: dropH }"
        @move="persist"
        @resize="persist"
        @drop="onGridDrop"
        @settled="putDropBox"
      >
        <template #item="{ item }">
          <div v-if="getPlacement(item.id)" :ref="(el) => watchTile(item.id, el as Element | null)" class="page-cell" :data-placement="item.id">
            <PageTile
              :view="getPlacement(item.id)!.view"
              :data="placementData[item.id] ?? null"
              :mounted="isMounted(item.id)"
              :page-view="getPlacement(item.id)!.sv"
              :editable="editable"
              @remove="removePlacement(getPlacement(item.id)!.sv, getPlacement(item.id)!.view)"
              @settings="openPlacement(getPlacement(item.id)!.sv, getPlacement(item.id)!.view)"
            />
          </div>
          <MarEmptyState v-else icon="eye" severity="neutral" title="View not loaded" />
        </template>
        <template #drop>
          <ViewPreview v-if="dragView" :key="dragView.id" :view="dragView" />
        </template>
      </MarDynamicGrid>
    </div>
    <small v-if="hidden > 0" class="page-hidden">
      {{ hidden }} more placement{{ hidden === 1 ? '' : 's' }} on this page, single mode shows one
    </small>
  </div>
</template>

<style scoped>
.root {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.page-grid {
  flex: 1;
  min-height: 0;
  overflow: auto;
  transition: background-color 0.2s ease;
}

.root-flow {
  height: auto;
}

.page-grid-flow {
  flex: none;
  overflow: visible;
}

.page-cell {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.page-hidden {
  flex: none;
  padding: 0.4em 0.9em;
  color: var(--mar-text-muted-color);
}
</style>
